import { ResponsiveBreakpoint } from '../components/organisms/AtlasComponent/AtlasViewContext';

export interface MoleculeVisibilityConfig {
  isVisible: boolean;
  maxWidth?: string;
  minWidth?: string;
  className?: string;
}

/**
 * Determines if a molecule should be visible and its styling based on responsive breakpoint
 * @param moleculeType - The type of molecule (NodeLabel, PerspectiveSelector, etc.)
 * @param isImportant - Whether the molecule has important: true in moleculeProps
 * @param breakpoint - Current responsive breakpoint
 * @param moleculeProps - Additional molecule properties
 * @returns Configuration for visibility and styling
 */
const getImportanceBasedVisibility = (
  isImportant: boolean,
  breakpoint: ResponsiveBreakpoint,
  mobileMinWidth: string
): MoleculeVisibilityConfig => {
  switch (breakpoint) {
    case 'full':
      return { isVisible: true };
    case 'compact':
      return { isVisible: isImportant };
    case 'minimal':
      return { isVisible: isImportant, className: 'compressed' };
    case 'mobile':
      return { isVisible: isImportant, className: 'compressed', minWidth: mobileMinWidth };
    default:
      return { isVisible: true };
  }
};

export const getMoleculeVisibility = (
  moleculeType: string,
  isImportant = false,
  breakpoint: ResponsiveBreakpoint,
  moleculeProps?: any
): MoleculeVisibilityConfig => {

  // NodeLabel (Name) - Always visible with different width constraints
  if (moleculeType === 'NodeLabel') {
    switch (breakpoint) {
      case 'full':
        return { isVisible: true };
      case 'compact':
        return { isVisible: true, maxWidth: '250px' };
      case 'minimal':
        return { isVisible: true, maxWidth: '180px' };
      case 'mobile':
        return { isVisible: true, minWidth: '150px' };
      default:
        return { isVisible: true };
    }
  }

  // PerspectiveSelector - Always visible (if configured in atlas, it should show)
  if (moleculeType === 'PerspectiveSelector') {
    return { isVisible: true };
  }

  // Aspect (formerly AtlasDateSelector / AtlasTextColumn, collapsed in EAINT-866) -
  // Similar to PerspectiveSelector
  if (moleculeType === 'Aspect') {
    return getImportanceBasedVisibility(isImportant, breakpoint, '100px');
  }

  // NodeDetailsButton (Description) - Always visible
  if (moleculeType === 'NodeDetailsButton') {
    return { isVisible: true };
  }

  // ArcStageSelector (legacy alias: FlowStageSelector) - Always visible
  if (moleculeType === 'ArcStageSelector' || moleculeType === 'FlowStageSelector') {
    return { isVisible: true };
  }

  // Status icons, pills, etc. - Always visible
  if (['NodePerspectivePill', 'NodeCounts', 'Status', 'ApproachBadges'].includes(moleculeType)) {
    return { isVisible: true };
  }

  // Default behavior - show all on full, important-only on smaller screens
  switch (breakpoint) {
    case 'full':
      return { isVisible: true };
    case 'compact':
    case 'minimal':
    case 'mobile':
      return { isVisible: isImportant };
    default:
      return { isVisible: true };
  }
};

/**
 * Gets responsive class names for Atlas components with optimized space utilization
 * @param breakpoint - Current responsive breakpoint
 * @param isToolbarExpanded - Whether toolbar is expanded
 * @param viewportWidth - Current viewport width for fine-tuned optimization
 * @returns CSS class names for the atlas component
 */
export const getAtlasResponsiveClasses = (
  breakpoint: ResponsiveBreakpoint,
  isToolbarExpanded: boolean,
  viewportWidth?: number
): string => {
  const baseClass = 'atlas-component pt-1';
  const currentViewportWidth = viewportWidth || window.innerWidth;

  // Full width for actual mobile devices (max-width: 768px)
  if (currentViewportWidth <= 768) {
    return `${baseClass} col`;
  }

  // Optimized space utilization for large screens
  if (isToolbarExpanded) {
    switch (breakpoint) {
      case 'full':
        // Dynamic width based on actual screen size for optimal space usage
        if (currentViewportWidth >= 3440) {
          return `${baseClass} col-10`; // Ultrawide: Use more space (83.33%)
        } else if (currentViewportWidth >= 2560) {
          return `${baseClass} col-9`; // 4K: Generous space (75%)
        } else if (currentViewportWidth >= 1920) {
          return `${baseClass} col-8`; // Full HD+: Better utilization (75%)
        } else {
          return `${baseClass} col-8`; // Standard desktop (66.67%)
        }
      case 'compact':
        // Less aggressive reduction for compact screens when toolbar expanded
        if (currentViewportWidth >= 2560) {
          return `${baseClass} col-8`; // 4K+: Still generous (66.67%)
        } else {
          return `${baseClass} col-7`; // Standard: Moderate reduction (58.33%)
        }
      case 'minimal':
        return `${baseClass} col-6`;
      case 'mobile':
        return `${baseClass} col-5`;
      default:
        return `${baseClass} col-8`;
    }
  }

  // When toolbar is collapsed, maximize space utilization
  switch (breakpoint) {
    case 'full':
      // Near-full width utilization on all screen sizes (91.67%)
      return `${baseClass} col-11`;
    case 'compact':
      return `${baseClass} col-10`;
    case 'minimal':
      return `${baseClass} col-9`;
    case 'mobile':
      return `${baseClass} col-8`;
    default:
      return `${baseClass} col-11`;
  }
};